'use client';

import React, { useState } from 'react';
import { Hand, X, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { useHandRaiseQueue, HandRaiseQueueItem } from './HandRaiseQueueContext';
import { useRoomContext } from '@livekit/components-react';
import { useHostControl } from './HostControlContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface HandRaiseQueuePanelProps {
  isOpen: boolean;
  onToggle: () => void;
}

export function HandRaiseQueuePanel({ isOpen, onToggle }: HandRaiseQueuePanelProps) {
  const { handRaiseQueue, removeFromQueue, clearQueue, getQueueCount } = useHandRaiseQueue();
  const room = useRoomContext();
  const [isExpanded, setIsExpanded] = useState(true);
  const { isHost, hasModeratorPermissions } = useHostControl();

  const queueCount = getQueueCount();
  const canManageQueue = isHost || hasModeratorPermissions;

  const handleLowerHand = (participantId: string) => {
    removeFromQueue(participantId);

    try {
      const data = {
        type: 'lower-hand-command',
        targetParticipant: participantId,
        fromHost: room?.localParticipant?.identity,
        timestamp: Date.now(),
      };

      room?.localParticipant?.publishData(new TextEncoder().encode(JSON.stringify(data)), {
        topic: 'hand-raise-management',
      });
    } catch (error) {
      console.error('Erro ao enviar comando para baixar mão:', error);
    }
  };

  const handleClearAll = () => {
    if (window.confirm('Tem certeza que deseja limpar toda a fila de mãos levantadas?')) {
      clearQueue();

      try {
        const data = {
          type: 'clear-all-hands',
          fromHost: room?.localParticipant?.identity,
          timestamp: Date.now(),
        };

        room?.localParticipant?.publishData(new TextEncoder().encode(JSON.stringify(data)), {
          topic: 'hand-raise-management',
        });
      } catch (error) {
        console.error('Erro ao enviar comando para limpar todas as mãos:', error);
      }
    }
  };

  const formatTime = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    if (minutes > 0) {
      return `${minutes}m ${seconds}s atrás`;
    }
    return `${seconds}s atrás`;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed top-16 right-4 w-80 bg-neutral-800 border border-neutral-700 rounded-lg shadow-lg z-50"
      style={{ maxHeight: '70vh' }}
    >
      <Card className="bg-neutral-800 border-neutral-700 text-white">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Hand size={20} className="text-yellow-400" />
              <CardTitle className="text-lg">Mãos Levantadas</CardTitle>
              {queueCount > 0 && (
                <Badge variant="secondary" className="bg-yellow-400 text-black">
                  {queueCount}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1 h-8 w-8 text-neutral-400 hover:text-white"
              >
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggle}
                className="p-1 h-8 w-8 text-neutral-400 hover:text-white"
              >
                <X size={16} />
              </Button>
            </div>
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent className="pt-0">
            {queueCount === 0 ? (
              <div className="text-center py-8 text-neutral-400">
                <Users size={32} className="mx-auto mb-2 opacity-50" />
                <p>Nenhuma mão levantada</p>
              </div>
            ) : (
              <>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {handRaiseQueue.map((item, index) => (
                    <QueueItem
                      key={item.participantId}
                      item={item}
                      position={index + 1}
                      onLowerHand={canManageQueue ? handleLowerHand : undefined}
                      formatTime={formatTime}
                    />
                  ))}
                </div>
              </>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}

interface QueueItemProps {
  item: HandRaiseQueueItem;
  position: number;
  onLowerHand?: (participantId: string) => void;
  formatTime: (timestamp: number) => string;
}

function QueueItem({ item, position, onLowerHand, formatTime }: QueueItemProps) {
  return (
    <div className="flex items-center justify-between p-3 bg-neutral-700 rounded-lg">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex-shrink-0">
          <Badge
            variant="outline"
            className="w-6 h-6 rounded-full flex items-center justify-center text-xs border-yellow-400 text-yellow-400"
          >
            {position}
          </Badge>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-white truncate">{item.participantName}</p>
            {item.isLocal && (
              <Badge variant="secondary" className="text-xs bg-blue-600 text-white">
                Você
              </Badge>
            )}
          </div>
          <p className="text-xs text-neutral-400">{formatTime(item.timestamp)}</p>
        </div>
      </div>

      {/* {onLowerHand && !item.isLocal && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onLowerHand(item.participantId)}
          className="p-1 h-8 w-8 text-neutral-400 hover:text-red-400 hover:bg-red-400/10"
          title="Baixar mão"
        >
          <Hand size={14} className="rotate-180" />
        </Button>
      )} */}
    </div>
  );
}
